import { NgHybridStateDeclaration } from '@uirouter/angular-hybrid';

import { CodeTypeModel }                            from '../core/models/code-type';
import { TableService }                             from '../table/table.service';
import { TransactionCenterBookingModel }            from './bookings/typings/transaction-center.booking';
import { TransactionCenterBookingsComponent }       from './bookings/transaction-center.bookings.component';
import { TransactionCenterBookingsService }         from './bookings/transaction-center.bookings.service';
import { TransactionCenterComponent }               from './transaction-center.component';
import { TransactionCenterInspectionModel }         from './inspections/typings/transaction-center.inspection';
import { TransactionCenterInspectionsComponent }    from './inspections/transaction-center.inspections.component';
import { TransactionCenterInspectionsService }      from './inspections/transaction-center.inspections.service';
import { TransactionCenterJobModel }                from './jobs/typings/transaction-center.job';
import { TransactionCenterJobsComponent }           from './jobs/transaction-center.jobs.component';
import { JobsService }                              from './jobs/transaction-center.jobs.service';
import { TransactionCenterOrderModel }              from './orders/typings/transaction-center.order';
import { TransactionCenterOrdersComponent }         from './orders/transaction-center.orders.component';
import { TransactionCenterOrdersService }           from './orders/transaction-center.orders.service';
import { TransactionCenterVendorInvoiceModel }      from './vendor-invoices/typings/transaction-center.vendor-invoice';
import { TransactionCenterVendorInvoicesComponent } from './vendor-invoices/transaction-center.vendor-invoices.component';
import { TransactionCenterVendorInvoicesService }   from './vendor-invoices/transaction-center.vendor-invoices.service';

export const TransactionCenterStates : Array<NgHybridStateDeclaration> = [
  {
    component : TransactionCenterComponent,
    name      : 'app.transaction-center',
    redirectTo: 'app.transaction-center.jobs',
    url       : '/transaction-center'
  },
  {
    component: TransactionCenterBookingsComponent,
    data     : {
      tableCacheKey: 'transactionCenterBookings'
    },
    name   : 'app.transaction-center.bookings',
    resolve: [
      {
        deps     : [ TableService, TransactionCenterBookingsService ],
        resolveFn: ( tableService : TableService, bookingsService: TransactionCenterBookingsService ) : Promise<Array<TransactionCenterBookingModel>> => {
          const cache = tableService.getCacheForTable(bookingsService.tableCacheKey);

          return bookingsService.get(cache.filtering, {
              direction: cache.sorting.direction,
              field    : cache.sorting.field,
              offset   : 0,
              pageSize : cache.paging.pageSize
            });
        },
        token: 'bookings'
      }
    ],
    url: '/bookings'
  },
  {
    component: TransactionCenterInspectionsComponent,
    data     : {
      tableCacheKey: 'transactionCenterInspections'
    },
    name   : 'app.transaction-center.inspections',
    resolve: [
      {
        deps     : [ TableService, TransactionCenterInspectionsService ],
        resolveFn: ( tableService : TableService, inspectionsService : TransactionCenterInspectionsService ) : Promise<Array<TransactionCenterInspectionModel>> => {
          const cache = tableService.getCacheForTable(inspectionsService.tableCacheKey);

          return inspectionsService.get({
              status: cache.filtering.status || 'D',
              value : cache.filtering.value  || '*'
            }, {
              field    : cache.sorting.field     || 'inspection_date',
              direction: cache.sorting.direction || 'desc',
              pageSize : cache.paging.pageSize   || 10,
              offset   : 0
            });
        },
        token: 'inspections'
      },
      {
        deps     : [ TransactionCenterInspectionsService ],
        resolveFn: ( inspectionsService : TransactionCenterInspectionsService ) : Promise<Array<CodeTypeModel>> => inspectionsService.getStatuses(),
        token    : 'statuses'
      }
    ],
    url: '/inspections'
  },
  {
    component: TransactionCenterJobsComponent,
    data     : {
      tableCacheKey: 'transactionCenterJobs'
    },
    name   : 'app.transaction-center.jobs',
    resolve: [
      {
        deps     : [ JobsService ],
        resolveFn: ( jobsService : JobsService ) : Promise<Array<CodeTypeModel>> => jobsService.getConstraints(),
        token    : 'constraints'
      },
      {
        deps     : [ JobsService ],
        resolveFn: ( jobsService : JobsService ) : Promise<Array<CodeTypeModel>> => jobsService.getStatusOptions(),
        token    : 'jobStatusOptions'
      },
      {
        deps     : [ JobsService ],
        resolveFn: ( jobsService : JobsService ) : Promise<Array<CodeTypeModel>> => jobsService.getStatuses(),
        token    : 'jobStatuses'
      },
      {
        deps     : [ JobsService, TableService ],
        resolveFn: ( jobsService : JobsService, tableService : TableService ) : Promise<Array<TransactionCenterJobModel>> => {
          const cache = tableService.getCacheForTable(jobsService.tableCacheKey);

          return jobsService.get(cache.filtering, {
              direction: cache.sorting.direction,
              field    : cache.sorting.field,
              offset   : 0,
              pageSize : cache.paging.pageSize
            });
        },
        token: 'jobs'
      }
    ],
    url: '/jobs'
  },
  {
    component: TransactionCenterOrdersComponent,
    data     : {
      tableCacheKey: 'transactionCenterOrders'
    },
    name   : 'app.transaction-center.orders',
    resolve: [
      {
        deps     : [ TableService, TransactionCenterOrdersService ],
        resolveFn: ( tableService : TableService, ordersService : TransactionCenterOrdersService ) : Promise<Array<TransactionCenterOrderModel>> => {
          const cache = tableService.getCacheForTable(ordersService.tableCacheKey);

          return ordersService.get(cache.filtering, {
              direction: cache.sorting.direction,
              field    : cache.sorting.field,
              offset   : 0,
              pageSize : cache.paging.pageSize
            });
        },
        token: 'orders'
      },
      {
        deps     : [ TransactionCenterOrdersService ],
        resolveFn: ( ordersService : TransactionCenterOrdersService ) : Promise<Array<CodeTypeModel>> => ordersService.getStatuses(),
        token    : 'statuses'
      }
    ],
    url: '/orders'
  },
  {
    component: TransactionCenterVendorInvoicesComponent,
    data     : {
      tableCacheKey: 'transactionCenterVendorInvoices'
    },
    name   : 'app.transaction-center.vendor-invoices',
    resolve: [
      {
        deps     : [ TableService, TransactionCenterVendorInvoicesService ],
        resolveFn: ( tableService : TableService, vendorInvoicesService : TransactionCenterVendorInvoicesService ) : Promise<Array<TransactionCenterVendorInvoiceModel>> => {
          const cache = tableService.getCacheForTable(vendorInvoicesService.tableCacheKey);

          return vendorInvoicesService.get(cache.filtering, {
              direction: cache.sorting.direction,
              field    : cache.sorting.field,
              offset   : 0,
              pageSize : cache.paging.pageSize
            });
        },
        token: 'invoices'
      },
      {
        deps     : [ TransactionCenterVendorInvoicesService ],
        resolveFn: ( vendorInvoicesService : TransactionCenterVendorInvoicesService ) : Promise<Array<CodeTypeModel>> => vendorInvoicesService.getStatuses(),
        token    : 'statuses'
      }
    ],
    url: '/vendor-invoices'
  }
];
